import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowLeft, FileText, Eye } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SummaryModal } from "@/components/SummaryModal";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface SummaryHistoryItem {
  id: string;
  case_title: string;
  case_metadata: any;
  summary_data: any;
  created_at: string;
}

interface SummaryHistoryProps {
  onBack: () => void;
}

export const SummaryHistory = ({ onBack }: SummaryHistoryProps) => {
  const { user } = useAuth();
  const [summaries, setSummaries] = useState<SummaryHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSummary, setSelectedSummary] = useState<SummaryHistoryItem | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSummaryHistory();
    }
  }, [user]);

  const fetchSummaryHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('summary_history')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching summary history:', error);
        return;
      }

      setSummaries(data || []);
    } catch (error) {
      console.error('Error fetching summary history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSummary = (summary: SummaryHistoryItem) => {
    setSelectedSummary(summary);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={onBack} className="p-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Historial de Resúmenes</h1>
        </div>
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="h-4 bg-muted/60 rounded animate-pulse" />
                <div className="h-3 bg-muted/40 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-muted/40 rounded animate-pulse w-1/2" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={onBack} className="p-2">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold text-foreground">Historial de Resúmenes</h1>
      </div>

      {summaries.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No hay resúmenes guardados
            </h3>
            <p className="text-muted-foreground">
              Los resúmenes que generes aparecerán aquí para que puedas revisarlos más tarde.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {summaries.map((summary) => (
            <Card key={summary.id} className="hover:shadow-md transition-shadow cursor-pointer">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg text-foreground mb-2">
                      {summary.case_title}
                    </CardTitle>
                    {summary.case_metadata?.carátula && (
                      <p className="text-sm text-muted-foreground mb-1">
                        <strong>Carátula:</strong> {summary.case_metadata.carátula}
                      </p>
                    )}
                    {summary.case_metadata?.fecha && (
                      <p className="text-sm text-muted-foreground mb-1">
                        <strong>Fecha:</strong> {summary.case_metadata.fecha}
                      </p>
                    )}
                    {summary.case_metadata?.tribunal && (
                      <p className="text-sm text-muted-foreground">
                        <strong>Tribunal:</strong> {summary.case_metadata.tribunal}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleViewSummary(summary)}
                    className="ml-4"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Ver resumen
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3 mr-1" />
                  {format(new Date(summary.created_at), "d 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedSummary && (
        <SummaryModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          summaryData={selectedSummary.summary_data}
          selectedCase={selectedSummary.case_metadata}
        />
      )}
    </div>
  );
};